import { StepIndicator } from '@kcm/ui'
import { Outlet, useLocation } from 'react-router-dom'
import { useTranslate } from '@tolgee/react'
import { resolveBanners } from '@kcm/contracts'
import { PageShell } from './PageShell'
import { BannerSlider } from './BannerSlider'
import { useSite } from './SiteProvider'
import { useLocale } from '../hooks/useLocale'
import { useResume } from '../hooks/useResume'

/** The four steps of PLAN.md §13, in order. Index + 1 is the step number. */
export const STEP_PATHS = ['/', '/identity', '/compliance', '/done'] as const

/** Which step a path belongs to. `/verify/return` and `/confirm` are step 2. */
export function stepForPath(pathname: string): number {
  // Both are the tail of step 2 — the Didit round trip and confirming what it
  // read — so the indicator should still show Identity, not jump ahead.
  if (pathname.startsWith('/verify') || pathname.startsWith('/confirm')) return 2
  const index = STEP_PATHS.indexOf(pathname as (typeof STEP_PATHS)[number])
  return index === -1 ? 1 : index + 1
}

export function pathForStep(step: number): string {
  return STEP_PATHS[Math.min(Math.max(step, 1), STEP_PATHS.length) - 1] ?? '/'
}

/**
 * Chrome shared by every step: who this site is, and how far along the user is.
 *
 * The step indicator is not decoration. §11.5 attributes part of the 40%
 * abandonment to a flow that never told people how much was left, and the
 * legacy funnel showed no progress at all.
 */
export function FunnelLayout() {
  const { pathname } = useLocation()
  const { t } = useTranslate()
  const step = stepForPath(pathname)

  // Sends a returning user to wherever the server says they got to, and pushes
  // a deep link back if it runs ahead of that.
  useResume()

  const labels = [t('step.account'), t('step.identity'), t('step.compliance'), t('step.finish')]

  return (
    <PageShell>
      <StepIndicator current={step} steps={labels} />
      <Outlet />
    </PageShell>
  )
}

/**
 * The same chrome without the progress bar, for the screens that are not part
 * of an application: signing in and getting a password back. `useResume` is
 * deliberately absent — someone on their way to the cabinet must not be pulled
 * into a half-finished funnel session left in this browser.
 */
export function AuthLayout() {
  const { pathname } = useLocation()
  const site = useSite()
  const locale = useLocale()
  return (
    <PageShell>
      {/* The marketing hero belongs on the sign-in page only, not the funnel
          steps or password reset. Banners follow the chosen language, falling
          back to the English set when a locale has none of its own. */}
      {pathname === '/login' && <BannerSlider banners={resolveBanners(site, locale)} />}
      <Outlet />
    </PageShell>
  )
}
